import { DCEvent } from "@/events/eventsDomain";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { useDispatch, useI18n } from "@/stores/RootStoreContext";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  Modifier,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useDndMonitor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { isArray } from "lodash";
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
} from "react";

export interface DragAndDrop<T> {
  canDrag?: (row: T) => boolean;
  canDrop?: (draggedRow: T, targetRow: T) => boolean;
  renderDragOverlay?: (draggedRow: T, i18n: PreppedI18nState) => ReactNode;
  renderDropOverlay?: (draggedRow: T, targetRow: T, i18n: PreppedI18nState) => ReactNode;
  onDrop: ((draggedRow: T, targetRow: T) => void) | ((draggedRow: T, targetRow: T) => DCEvent[]);
}

const DndAreaContext = createContext(false);
const DndScopeContext = createContext<string | undefined>(undefined);

const eventCoordinates = (event: Event): { x: number; y: number } | undefined => {
  switch (true) {
    case event instanceof MouseEvent:
      return { x: event.clientX, y: event.clientY };
    case typeof TouchEvent !== "undefined" && event instanceof TouchEvent: {
      const touch = event.touches[0] ?? event.changedTouches[0];
      return touch ? { x: touch.clientX, y: touch.clientY } : undefined;
    }
    default:
      return undefined;
  }
};

const snapToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
  const coords = activatorEvent && eventCoordinates(activatorEvent);
  if (!coords || !draggingNodeRect) return transform;
  return {
    ...transform,
    x: transform.x + coords.x - draggingNodeRect.left + 4,
    y: transform.y + coords.y - draggingNodeRect.top + 4,
  };
};

const useDndSensors = () =>
  useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

/**
 * Shared drag-and-drop area. Components with a `dragAndDrop` config rendered inside
 * a DndArea join its DndContext, so rows can be dragged from one component and
 * dropped on another. The drop is handled by the receiving component's `onDrop`.
 */
export function DndArea({ children }: PropsWithChildren): ReactElement {
  const sensors = useDndSensors();
  return (
    <DndAreaContext.Provider value={true}>
      <DndContext sensors={sensors} collisionDetection={pointerWithin}>
        {children}
      </DndContext>
    </DndAreaContext.Provider>
  );
}

const useDragAndDropHandlers = <T,>(dragAndDrop: DragAndDrop<T>) => {
  const dispatch = useDispatch();
  const scopeId = useId();
  const [draggedRow, setDraggedRow] = useState<T | undefined>(undefined);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current;
      if (data?.scopeId === scopeId) setDraggedRow(data.row);
    },
    [scopeId],
  );

  const onDragCancel = useCallback(() => setDraggedRow(undefined), []);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggedRow(undefined);
      const overData = event.over?.data.current;
      if (!event.over || overData?.scopeId !== scopeId) return;
      const draggedRow: T | undefined = event.active.data.current?.row;
      const targetRow: T | undefined = overData.row;
      if (draggedRow === undefined || targetRow === undefined) return;
      if (event.active.id === event.over.id) return;
      if (dragAndDrop.canDrop && !dragAndDrop.canDrop(draggedRow, targetRow)) return;
      const result = dragAndDrop.onDrop(draggedRow, targetRow);
      if (isArray(result)) {
        dispatch(result);
      }
    },
    [dragAndDrop, dispatch, scopeId],
  );

  return { scopeId, draggedRow, onDragStart, onDragEnd, onDragCancel };
};

type ScopeProps<T> = PropsWithChildren<{ dragAndDrop: DragAndDrop<T> }>;

type ScopeContentProps<T> = ScopeProps<T> & { scopeId: string; draggedRow: T | undefined };

const ScopeContent = <T,>({
  scopeId,
  draggedRow,
  dragAndDrop,
  children,
}: ScopeContentProps<T>): ReactElement => {
  const i18n = useI18n();
  return (
    <DndScopeContext.Provider value={scopeId}>
      {children}
      {dragAndDrop.renderDragOverlay && draggedRow !== undefined && (
        <DragOverlay modifiers={[snapToCursor]} dropAnimation={null}>
          {dragAndDrop.renderDragOverlay(draggedRow, i18n)}
        </DragOverlay>
      )}
    </DndScopeContext.Provider>
  );
};

const StandaloneScope = <T,>({ dragAndDrop, children }: ScopeProps<T>): ReactElement => {
  const sensors = useDndSensors();
  const { scopeId, draggedRow, onDragStart, onDragEnd, onDragCancel } =
    useDragAndDropHandlers(dragAndDrop);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <ScopeContent scopeId={scopeId} draggedRow={draggedRow} dragAndDrop={dragAndDrop}>
        {children}
      </ScopeContent>
    </DndContext>
  );
};

const MonitoredScope = <T,>({ dragAndDrop, children }: ScopeProps<T>): ReactElement => {
  const { scopeId, draggedRow, onDragStart, onDragEnd, onDragCancel } =
    useDragAndDropHandlers(dragAndDrop);
  useDndMonitor(
    useMemo(
      () => ({ onDragStart, onDragEnd, onDragCancel }),
      [onDragStart, onDragEnd, onDragCancel],
    ),
  );
  return (
    <ScopeContent scopeId={scopeId} draggedRow={draggedRow} dragAndDrop={dragAndDrop}>
      {children}
    </ScopeContent>
  );
};

/**
 * Provides drag-and-drop for the rows of a single component. Owns its DndContext
 * when standalone; joins the surrounding DndContext when inside a DndArea.
 */
export const DragAndDropScope = <T,>({ dragAndDrop, children }: ScopeProps<T>): ReactElement => {
  const inArea = useContext(DndAreaContext);
  return inArea ? (
    <MonitoredScope dragAndDrop={dragAndDrop}>{children}</MonitoredScope>
  ) : (
    <StandaloneScope dragAndDrop={dragAndDrop}>{children}</StandaloneScope>
  );
};

export const useDraggableRow = <T,>(
  row: T,
  rowId: string,
  dragAndDrop: DragAndDrop<T>,
): {
  setRefs: (el: HTMLElement | null) => void;
  listeners: ReturnType<typeof useDraggable>["listeners"];
  isDragging: boolean;
  isOver: boolean;
  draggedRow: T | undefined;
  isDropTarget: boolean;
  transform: { x: number; y: number } | null;
} => {
  const scopeId = useContext(DndScopeContext) ?? "";
  const id = `${scopeId}:${rowId}`;
  const canDrag = dragAndDrop.canDrag?.(row) ?? true;
  const {
    active,
    isDragging,
    listeners,
    setNodeRef: setDraggableRef,
    transform,
  } = useDraggable({ id, data: { row, scopeId }, disabled: !canDrag });
  const draggedRow: T | undefined = active?.data.current?.row;
  const isDropTarget =
    draggedRow !== undefined &&
    active?.id !== id &&
    (dragAndDrop.canDrop?.(draggedRow, row) ?? true);
  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id,
    data: { row, scopeId },
    disabled: !isDropTarget,
  });
  const setRefs = useCallback(
    (el: HTMLElement | null) => {
      setDraggableRef(el);
      setDroppableRef(el);
    },
    [setDraggableRef, setDroppableRef],
  );
  const followsCursor = !dragAndDrop.renderDragOverlay;
  return {
    setRefs,
    listeners,
    isDragging,
    isOver,
    draggedRow,
    isDropTarget,
    transform: followsCursor ? transform : null,
  };
};

export const dropOverlayClassNames =
  "absolute inset-0 rounded-lg ring-2 ring-primary pointer-events-none z-2";
